import { API_PREFIX, ErrorResponse, TokenPair } from "@spf/contracts";
import { ApiError } from "./errors.js";
const UNREACHABLE = {
    error: { code: "unreachable", message: "The server could not be reached." },
};
function headersFor(init, accessToken) {
    const headers = new Headers(init.headers);
    if (init.body !== undefined)
        headers.set("content-type", "application/json");
    if (init.authenticated !== false && accessToken !== null) {
        headers.set("authorization", `Bearer ${accessToken}`);
    }
    return headers;
}
function failure(status, body) {
    const parsed = ErrorResponse.safeParse(body);
    return parsed.success
        ? new ApiError(status, parsed.data.error.code, parsed.data.error.message)
        : new ApiError(status, "unknown", "Request failed.");
}
/**
 * Everything about talking to the API that is not a specific endpoint: sending
 * a request, renewing an expired access token, and deciding when a failure
 * means the session is over.
 */
export function createTransport(options) {
    const baseUrl = options.baseUrl ?? "";
    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    /**
     * Rotation makes a refresh token single use, and the server treats a second
     * presentation of a spent one as theft and revokes the whole session. Two
     * views loading at once would do exactly that, so concurrent refreshes share
     * one attempt. This is a correctness requirement, not an optimisation.
     */
    let inFlight = null;
    async function send(path, init, accessToken) {
        const response = await doFetch(`${baseUrl}${path}`, {
            ...init,
            headers: headersFor(init, accessToken),
        });
        const text = await response.text();
        return { response, body: text.length > 0 ? JSON.parse(text) : null };
    }
    async function rotate(refreshToken) {
        let received;
        try {
            received = await send(`${API_PREFIX}/auth/refresh`, { method: "POST", body: JSON.stringify({ refreshToken }), authenticated: false }, null);
        }
        catch {
            return { kind: "unreachable" };
        }
        if (!received.response.ok)
            return { kind: "rejected" };
        // A 200 that does not match the contract is not a usable session either.
        const parsed = TokenPair.safeParse(received.body);
        if (!parsed.success)
            return { kind: "rejected" };
        const tokens = {
            accessToken: parsed.data.accessToken,
            refreshToken: parsed.data.refreshToken,
        };
        options.onTokens(tokens);
        return { kind: "rotated", tokens };
    }
    function rotateOnce() {
        const current = options.getTokens();
        if (current === null)
            return Promise.resolve({ kind: "rejected" });
        inFlight ??= rotate(current.refreshToken).finally(() => {
            inFlight = null;
        });
        return inFlight;
    }
    /** The 401 path: refresh if we can, retry once, sign out if we cannot. */
    async function recover(path, init, sentWith) {
        // Another request may have rotated while this one was in the air. Retrying
        // with what is now stored avoids a second rotation for the same expiry.
        const stored = options.getTokens();
        if (stored !== null && stored.accessToken !== sentWith) {
            return send(path, init, stored.accessToken);
        }
        const rotation = await rotateOnce();
        if (rotation.kind === "unreachable") {
            return { response: new Response(null, { status: 503 }), body: UNREACHABLE };
        }
        if (rotation.kind === "rejected") {
            options.onSignedOut?.();
            return { response: new Response(null, { status: 401 }), body: null };
        }
        return send(path, init, rotation.tokens.accessToken);
    }
    async function request(path, schema, init = {}) {
        const sentWith = options.getTokens()?.accessToken ?? null;
        let received = await send(path, init, sentWith);
        if (received.response.status === 401 && init.authenticated !== false) {
            received = await recover(path, init, sentWith);
            // Still refused with a fresh token: the token was not the problem.
            if (received.response.status === 401)
                options.onSignedOut?.();
        }
        if (!received.response.ok)
            throw failure(received.response.status, received.body);
        return schema.parse(received.body);
    }
    return {
        request,
        post: (body) => ({ method: "POST", body: JSON.stringify(body), authenticated: false }),
        // `authenticated` is left unset, which means the default: attach the
        // bearer token and rotate it on a 401. Copying `post` here would send the
        // settings editor's write with no credentials at all.
        put: (body) => ({ method: "PUT", body: JSON.stringify(body) }),
        tokens: options.getTokens,
        store: options.onTokens,
        signOut: () => options.onSignedOut?.(),
        renew: async () => {
            const rotation = await rotateOnce();
            if (rotation.kind === "rotated") {
                return { kind: "renewed", accessToken: rotation.tokens.accessToken };
            }
            // Unreachable is a network fault, not a revoked session: the caller
            // retries rather than signing anybody out.
            if (rotation.kind === "rejected")
                options.onSignedOut?.();
            return { kind: rotation.kind };
        },
        baseUrl,
        fetch: doFetch,
    };
}
//# sourceMappingURL=transport.js.map